import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

// Scrollt eine Settings-Card so weit, dass ihre Oberkante mit `topGap` px
// Abstand unter dem Top-Rand des inneren Scroll-Containers `.settings-content-
// area` sitzt. Wichtig: `scrollIntoView` darf hier NICHT verwendet werden,
// weil es ueber die ganze Vorfahren-Kette propagiert und auch den outer
// `.container-fluid.content-container` scrollt — in dem haengt der Settings-
// Sticky-Header, der dann aus dem Viewport wandert (2026-05-12 Bug).
// Pattern analog zu staffing-management-tutorial.config.ts:432-446.
async function scrollSettingsCardIntoView(
  cardAnchor: string,
  topGap = 80,
): Promise<void> {
  const card = document.querySelector(
    `[onboardingAnchor="${cardAnchor}"]`,
  ) as HTMLElement | null;
  const scroller = document.querySelector(
    '.settings-content-area',
  ) as HTMLElement | null;
  if (!card || !scroller) return;
  const sr = scroller.getBoundingClientRect();
  const cr = card.getBoundingClientRect();
  const offsetInScroller = cr.top - sr.top + scroller.scrollTop;
  const target = Math.max(0, offsetInScroller - topGap);
  if (Math.abs(scroller.scrollTop - target) > 4) {
    scroller.scrollTo({ top: target, behavior: 'auto' });
  }
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
}

// Oeffnet/schliesst den Business-Hours-Popover programmatisch fuer einen
// bestimmten Wochentag (DayOfWeek 0–6, Sonntag=0). Wird vom Calendar-Tour-
// Step benutzt, damit der User direkt sieht, wie das Inline-Popover fuer
// einen einzelnen Tag aussieht — der Voice-Over erklaert dann die einzelnen
// Felder am offenen Beispiel. Klick auf die Tile-Kachel triggert exakt den
// User-Code-Pfad (gleicher Handler wie ein echter Klick), kein DOM-Hack.
function clickBusinessHoursTile(dayOfWeek: number): void {
  const tile = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="business-hours"] [data-dow="${dayOfWeek}"]`,
  );
  tile?.click();
}

// Liest den vom `general-settings.component` gepflegten, vom Warning-Banner
// ENTKOPPELTEN sessionStorage-Snapshot der noch fehlenden Pflichtfelder.
// Inhalt = bereits uebersetzte, komma-separierte Labels (z.B.
// "Unternehmenstyp, Region/Bundesland"). Pure Snapshot-Read (kein
// Service-Lookup) — passt zum Vertrag von `TourStep.content`/`contentParams`.
// Der Component schreibt den Key bei jedem Daten-Load und jeder Form-
// Aenderung; valid → Key entfernt. Fehlt der Key (z.B. Tour ohne vorherigen
// Settings-Page-Besuch betreten) oder ist er leer, gilt "nichts offen" und
// der Abschluss-Step zeigt den normalen Glueckwunsch-Text.
function readCompanyInfoMissingFieldsLabel(): string {
  try {
    return (sessionStorage.getItem('companyInfoMissingFields') ?? '').trim();
  } catch {
    return '';
  }
}

function closeBusinessHoursPopover(): void {
  // Re-Klick auf die bereits aktive Tile toggelt activeDow zurueck auf null
  // (siehe `openPopover` im Editor-Component). Wir nehmen bewusst NICHT den
  // ESC-HostListener oder einen body-Klick: ESC im Document wuerde auch
  // den Tour-Popover-Step schliessen, und ein synthetischer body-Klick
  // landet je nach Phase im CDK-Overlay-Sub-Baum des Tour-Renderings und
  // greift dann nicht im Editor.
  const openTile = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="business-hours"] [data-dow][aria-expanded="true"]`,
  );
  openTile?.click();
}

/**
 * Welcome-/Setup-Tour V3: Firmen-Grunddaten konfigurieren.
 *
 * Fachliche 4-Step-Struktur (2026-05-11, Step 3 gesplittet aus Tester-Befund):
 *   1. Unternehmensinformationen — Firmenname, Unternehmenstyp,
 *      Organisationsstruktur. Cues pulsieren die drei Felder.
 *   2. Regionale Einstellungen — Land/Region/Zeitzone/Sprache.
 *   3a. Kalendereinstellungen — Wochenendtage + Geschäftsbeginn,
 *       Spotlight auf Calendar-Card.
 *   3b. Urlaubseinstellungen — Standard-Urlaubstage + Übertragung,
 *       Spotlight auf Vacation-Card, Tour-Abschluss mit Auto-Save-Hinweis.
 *
 * Step 3 wurde am 2026-05-11 gesplittet, weil:
 *   - Calendar-Card-Höhe (~941 px) sprengt den Viewport (~830 px), Popover
 *     in zentraler Position verdeckt erklärten Inhalt.
 *   - Kombinierter Voice-Over erwähnte "Klicken Sie jetzt Speichern", aber
 *     es gibt keinen Speichern-Button mehr (Auto-Save default).
 *   - SettingsSaveBar-Anchor ist trotz Namens der Page-Header oben, nicht
 *     ein Save-Action-Element — der Anchor war historisch falsch belegt.
 *
 * Vorher V2: 6 Steps, davon 4 mit nur einem Select plus 1 reiner Delegations-
 * Hinweis ("Kalender machen Sie spaeter"). V3 reduziert auf mentale Anker
 * (Wer — Wo — Wann — Wie viel) und nutzt VOICEOVER_CUES für Feld-Granularität.
 *
 * Version 3 + allowReplay: User im V1/V2-State werden beim naechsten
 * naturellen Trigger auf die V3-Tour umgeleitet. Die alten Step-IDs
 * (company-info-intro, company-type, work-structure, calendar-vacation-hint,
 * save-and-continue) sind verworfen — deren Translations-Keys und MP3s sind
 * obsolet, koennen beim naechsten Cleanup entfernt werden.
 */
export const COMPANY_INFO_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.CompanyInfo,
  version: 3,
  title: 'CHECKLIST.COMPANY_INFO.TITLE',
  description: 'CHECKLIST.COMPANY_INFO.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Tour fuehrt direkt durch Firmen-Einstellungs-Formulare. Ein User ohne
  // Edit-Recht auf die Firmen-Einstellungen kann den Schritt nicht
  // abschliessen — also gar nicht erst anbieten.
  requiredPermissions: [['Settings.CanEditCompanySettings']],
  triggers: [TourTrigger.FirstVisit, TourTrigger.Resume, TourTrigger.Manual],
  // Ziel MUSS `/settings/general` sein, nicht `/settings` (gemessen 25.08.2026).
  // `navigateToTourRoute` haelt die Route fuer erreicht, sobald
  // `currentUrl.startsWith(target + '/')` gilt (tour-coordinator.service.ts:3309).
  // Mit `/settings` traf das auf JEDE Unterseite zu: Wer auf
  // `/settings/subscription` oder `/settings/time-tracking` stand, wurde nicht
  // mehr weitergeleitet. Der Anchor `company-info` existiert aber nur auf
  // `/settings/general` — alle vier Steps liefen dann nacheinander in die
  // 8-Sekunden-Anchor-Zeitueberschreitung, und die Tour galt nach ~32 s als
  // abgeschlossen, ohne ein einziges Popover gezeigt zu haben.
  // Mit dem praezisen Ziel greift der Vergleich richtig: von einer anderen
  // Settings-Seite wird navigiert, auf `/settings/general` selbst nicht (dort
  // ist es Gleichheit). Der generische Praefix-Vergleich bleibt unangetastet —
  // er ist fuer Redirect-Ziele wie `/organization` genau richtig.
  triggerContext: { route: '/settings/general' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 168,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  priority: 10,
  // V2: 360 s. V3 auf ~90 s geschrumpft (3 × 20-35 s Voice-Over).
  estimatedDurationSec: 90,
  category: 'setup',
  chapters: [
    {
      id: 'main',
      title: 'CHECKLIST.COMPANY_INFO.TITLE',
      steps: [
        {
          // Anchor + Spotlight = ganze Basics-Card, placement: 'right' —
          // Popover sitzt mittig vertikal RECHTS neben der Karte mit dem
          // standard 14 px Abstand. Vorher war anchor `company-type-select`
          // (kleines Feld innerhalb der Karte) → Position-Strategy mit
          // `withPush` zog den Popover bei knappem Platz in die Karte
          // hinein und verdeckte die Form-Felder. Mit dem ganzen Card-
          // Anchor liegt das Popover verlaesslich ausserhalb der Karte
          // (ueberlappt ggf. die dimmte Regional-Karte rechts daneben —
          // akzeptabel, da nicht im Fokus). Voice-Over-Cues pulsieren
          // weiterhin nacheinander Firmenname, Unternehmenstyp und
          // Organisationsstruktur.
          id: 'company-info-basics',
          anchor: AnchorId.CompanyInfo,
          spotlightAnchor: AnchorId.CompanyInfo,
          title: 'TUTORIAL.SETTINGS.V3.BASICS.TITLE',
          content: 'TUTORIAL.SETTINGS.V3.BASICS.CONTENT',
          placement: 'right',
          showNextButton: true,
          hasCues: true,
        },
        {
          // Regionaler Rahmen: Land, Region, Zeitzone, Sprache zusammen.
          //
          // Anchor + Spotlight = ganze Regional-Card, placement: 'left'
          // — Popover sitzt mittig vertikal LINKS neben der Karte mit
          // 14 px Abstand. Vorher war anchor `country-select` (Land-Feld
          // innerhalb der Karte), das den Popover auf Höhe des kleinen
          // Felds positionierte und bei knappem Platz nach rechts
          // INTO die Karte gefallen ist (verdeckte Felder). Mit dem
          // ganzen Card-Anchor zentriert der Popover sich vertikal und
          // landet bei placement: 'left' verlässlich links neben der
          // Karte. Cues wandern weiterhin durchs Feld-Quartett.
          id: 'company-info-regional',
          anchor: AnchorId.RegionalSettings,
          spotlightAnchor: AnchorId.RegionalSettings,
          title: 'TUTORIAL.SETTINGS.V3.REGIONAL.TITLE',
          content: 'TUTORIAL.SETTINGS.V3.REGIONAL.CONTENT',
          placement: 'left',
          showNextButton: true,
          hasCues: true,
        },
        {
          // Step 3a — Kalendereinstellungen (Wochenendtage, Arbeitswoche-
          // Preset, individuelle Tagesstunden + Geschäftsbeginn pro Tag).
          //
          // Anchor = `business-hours` (der Wochentag-Strip + Inline-Popover-
          // Block — ~340 px hoch), Spotlight = ganze Calendar-Card (Ring um
          // alle relevanten Felder). placement: 'right' positioniert den
          // Tour-Popover an der rechten Kante des Anchor-Bereichs plus
          // 14 px Abstand — also knapp ausserhalb der Calendar-Card-Haupt-
          // breite, ueberlappt ggf. die dimmte Urlaubs-Karte. Wir wechseln
          // den Anchor weg vom `weekend-days-field` auf den Business-Hours-
          // Editor, weil der Voice-Over jetzt das geoeffnete Tages-Popover
          // erklaert und der Tour-Tooltip neben dem geoeffneten Tages-
          // Popover stehen soll, nicht oberhalb davon.
          //
          // `onBeforeShow` macht zwei Dinge:
          //   1. Scrollt die Karte 160 px unter den Top-Rand des inneren
          //      `.settings-content-area`-Containers (Helper am Dateianfang).
          //      Default-topGap (80 px) reichte hier nicht: nach dem Oeffnen
          //      des Tages-Popovers ist die Calendar-Card ~840 px hoch, und
          //      der `business-hours`-Anchor (Tour-Popover-Position) sitzt
          //      weit unten in der Card. Bei 80 px verschwand der Card-
          //      Header + "Erster Wochentag" hinter dem Settings-Sticky-
          //      Header. Mit 160 px bleibt der obere Bereich der Card
          //      sichtbar.
          //   2. Oeffnet das Inline-Popover fuer Dienstag (DayOfWeek 2) ueber
          //      einen synthetischen Klick auf die `[data-dow="2"]`-Tile.
          //      Damit sieht der User direkt, dass jeder Tag individuell
          //      einstellbar ist — der Voice-Over fuehrt am offenen Beispiel
          //      durch Geschaeftsbeginn, Geschaeftsende und "Auf Mo-Fr
          //      anwenden". Der RAF-Doppelschritt sichert, dass das Tile
          //      nach dem Scroll bereits im Layout liegt.
          //
          // `onAfterHide` schliesst das Tages-Popover wieder, damit der
          // Folge-Step (Urlaubseinstellungen) keinen halb-offenen Editor-
          // Rest auf der dimmte Calendar-Card sieht.
          id: 'company-info-calendar',
          anchor: AnchorId.BusinessHoursPerDay,
          spotlightAnchor: AnchorId.CalendarSettings,
          title: 'TUTORIAL.SETTINGS.V3.CALENDAR.TITLE',
          content: 'TUTORIAL.SETTINGS.V3.CALENDAR.CONTENT',
          placement: 'right',
          showNextButton: true,
          hasCues: true,
          onBeforeShow: async () => {
            await scrollSettingsCardIntoView('calendar-settings', 160);
            // Doppelter RAF: nach dem Scroll erst ein Frame fuer den Reflow,
            // dann ein zweiter, damit das per *ngIf gerenderte Popover
            // garantiert im DOM ist, bevor der naechste Step-Render den
            // Anchor-Rect misst.
            await new Promise<void>((r) => requestAnimationFrame(() => r()));
            clickBusinessHoursTile(2); // 2 = Dienstag (Sonntag=0)
            await new Promise<void>((r) => requestAnimationFrame(() => r()));
          },
          onAfterHide: () => closeBusinessHoursPopover(),
        },
        {
          // Step 3b — Urlaubseinstellungen + Tour-Abschluss.
          //
          // Anchor + Spotlight = ganze Vacation-Card, placement: 'left'
          // — Popover sitzt mittig vertikal LINKS neben der Karte mit
          // 14 px Abstand. Die Vacation-Card ist in der rechten Spalte,
          // links davon liegt die Kalender-Karte (in Step 4 nicht mehr
          // fokussiert). Popover landet im Bereich zwischen den beiden
          // Karten bzw. ueber der dimm-erfassten Kalender-Karte — der
          // Spotlight-Ring liegt eindeutig nur auf der Urlaubs-Karte, sodass
          // klar bleibt, worum es geht.
          //
          // `onBeforeShow` scrollt die Karte 80 px unter den Top-Rand des
          // inneren `.settings-content-area`-Containers (Helper am
          // Dateianfang), damit die ganze Karte + Popover-Höhe sichtbar
          // sind, auch wenn der User von weiter unten resumed. Direktes
          // scrollTo, kein scrollIntoView (sonst Outer-Container-Drift).
          id: 'company-info-vacation',
          // Analog zu Step 3: Anchor = kleines Feld in der Karte
          // (`standard-vacation-days`), Spotlight = ganze Vacation-Card.
          // placement: 'left' positioniert das Popover an der linken Kante
          // des Anchor-Felds — knapp ausserhalb der Urlaubs-Karte, ueberlappt
          // ggf. die dimmte Kalender-Karte. Ganze-Card-Anchor (~894 px)
          // hat hier dasselbe Off-Screen-Fallback-Problem ausgeloest wie
          // Step 3, deswegen Feld-Anchor.
          anchor: AnchorId.StandardVacationDays,
          spotlightAnchor: AnchorId.VacationSettings,
          // Titel ist statisch (das Tour-Step-Modell unterstuetzt keine
          // Titel-Funktion); der dynamische Hinweis steht im Body (`content`).
          title: 'TUTORIAL.SETTINGS.V3.VACATION.TITLE',
          // Abschluss-Step der Setup-Tour: Statt blind "Basis steht" zu
          // behaupten, prueft der Step den vom general-settings.component
          // gepflegten Snapshot der noch leeren Pflichtfelder und weist
          // dynamisch darauf hin ("Noch offen: Unternehmenstyp,
          // Bundesland"). Sind alle Pflichtfelder gesetzt, bleibt der
          // normale Abschluss-Text. Der Abschluss wird NICHT blockiert —
          // der Hinweis ist rein informativ (UX-Audit findings/02 #2).
          content: () =>
            readCompanyInfoMissingFieldsLabel()
              ? 'TUTORIAL.SETTINGS.V3.VACATION.CONTENT_OPEN_FIELDS'
              : 'TUTORIAL.SETTINGS.V3.VACATION.CONTENT',
          contentParams: () => ({ fields: readCompanyInfoMissingFieldsLabel() }),
          placement: 'left',
          showNextButton: true,
          hasCues: true,
          tone: 'info',
          onBeforeShow: () => scrollSettingsCardIntoView('vacation-settings'),
          // Funnel-Anschluss als expliziter CTA: schliesst die Unternehmens-
          // daten-Tour ab und startet direkt die naechste Setup-Tour „Erste
          // Abteilung". Ohne diesen Knopf reisst der Faden ab — der naechste
          // Schritt erschien bisher nur als leicht zu uebersehender Nudge-
          // Banner oben rechts bzw. erst nach manuellem Zurueck zum Dashboard
          // (UX-Audit findings/02 #4). Der Coordinator prueft vorab, ob die
          // Abteilungen-Tour startbar ist; sonst bleibt der gewohnte Nudge.
          actions: [
            {
              labelKey: 'TUTORIAL.SETTINGS.V3.VACATION.NEXT_TOUR_CTA',
              icon: 'fa-light fa-sitemap',
              variant: 'primary',
              startTour: TourFeature.FirstDepartment,
            },
          ],
        },
      ],
    },
  ],
};
